import { Skeleton } from "@/design-system/Skeleton";
import { useTranslation } from "@/translations";

import { EntityPreviewCardContainer } from "./EntityPreviewCardWithBackground";

type EntityPreviewCardWithCustomActionProps = {
  title: string;
  href?: string;
  onClick?: () => void;
  renderBadge?: () => React.ReactNode;
  renderAction: () => React.ReactNode;
} & (
  | {
      subtitle?: string;
      renderSubtitle?: never;
    }
  | {
      subtitle?: never;
      renderSubtitle: () => React.ReactNode | undefined;
    }
);

export function EntityPreviewCardWithCustomAction({
  title,
  href,
  onClick,
  subtitle,
  renderAction,
  renderSubtitle,
  renderBadge,
}: EntityPreviewCardWithCustomActionProps): React.ReactNode {
  return (
    <EntityPreviewCardContainer href={href} onClick={onClick}>
      <section className="flex flex-col items-center gap-space-sm p-space-md">
        {renderBadge?.()}
        <h4 className="line-clamp-1 text-body-emphasis">{title}</h4>
        {subtitle ? <p className="line-clamp-1 text-caption text-text-secondary">{subtitle}</p> : renderSubtitle?.()}
        {renderAction()}
      </section>
    </EntityPreviewCardContainer>
  );
}

export function EntityPreviewCardWithCustomActionSkeleton(): React.ReactNode {
  return (
    <EntityPreviewCardContainer>
      <section className="flex flex-col items-center gap-space-sm p-space-md">
        <Skeleton height="6" width="32" />
        <Skeleton height="6" width="1/3" />
        <Skeleton height="4" width="2/3" />
        <Skeleton height="10" />
      </section>
    </EntityPreviewCardContainer>
  );
}

type EntityPreviewCardWithCustomActionErrorProps = {
  title?: string;
  subtitle?: string;
};

export function EntityPreviewCardWithCustomActionError({
  title,
  subtitle,
}: EntityPreviewCardWithCustomActionErrorProps): React.ReactNode {
  const { t } = useTranslation();

  const titleOverride = title || t("component.community-post.entity-preview.title.not-available");
  const subtitleOverride = subtitle || t("component.community-post.entity-preview.subtitle.not-available");

  return (
    <section className="w-full *:text-text-secondary">
      <EntityPreviewCardWithCustomAction title={titleOverride} subtitle={subtitleOverride} renderAction={() => null} />
    </section>
  );
}
